import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowUp, Mail, MessageCircle, Phone } from "lucide-react";
import { textLang } from "@triple-h/shared";
import { Brand } from "./Brand";
import { useCategories, useSite } from "../hooks/useSite";
import { fallbackSite, phoneHref, viberHref } from "../lib/site";

export function Footer() {
  const { data: site = fallbackSite } = useSite();
  const { data: categories = [] } = useCategories();
  const year = new Date().getFullYear();

  function backToTop() {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  }

  return (
    <footer className="site-footer">
      <div className="container footer-main">
        <div className="footer-brand">
          <Brand tone="light" />
          <p className="footer-tagline" lang={textLang(site.tagline)}>{site.tagline || "Real Estate with Integrity"}</p>
          <p className="footer-burmese" lang="my">
            အိမ်၊ ခြံ၊ မြေနှင့် စီးပွားရေးအဆောက်အအုံများအတွက် ယုံကြည်စိတ်ချရသော အိမ်ခြံမြေဝန်ဆောင်မှု။
          </p>
          <Link className="footer-enquire" to="/contact">
            Start an enquiry <ArrowUpRight aria-hidden="true" />
          </Link>
        </div>

        <nav className="footer-column" aria-label="Footer property links">
          <h2>Properties</h2>
          <Link to="/properties">All properties</Link>
          <Link to="/gallery">Photo & video gallery</Link>
          <Link to="/properties?type=sale">For sale</Link>
          {categories.filter(c=>c.postCount).map((category) => (
            <Link key={category.id} to={`/properties?category=${encodeURIComponent(category.slug)}`}>
              <span lang={textLang(category.name)}>{category.name}</span>
            </Link>
          ))}
        </nav>

        <nav className="footer-column" aria-label="Footer company links">
          <h2>Company</h2>
          <Link to="/about">About Triple H</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/properties?type=sold">Sold properties</Link>
          <Link to="/admin">Admin sign in</Link>
        </nav>

        <div className="footer-column footer-contact">
          <h2>Contact</h2>
          <a href={phoneHref(site.publicPhone)}>
            <Phone aria-hidden="true" /> <span>{site.publicPhone}</span>
          </a>
          <a href={`mailto:${site.email}`}>
            <Mail aria-hidden="true" /> <span>{site.email}</span>
          </a>
          {site.viberUrl && (<a href={viberHref(site)}>
            <MessageCircle aria-hidden="true" /> <span>Viber</span>
          </a>)}
          {site.messengerUrl && (
            <a href={site.messengerUrl} target="_blank" rel="noreferrer">
              <MessageCircle aria-hidden="true" /> <span>Messenger</span>
            </a>
          )}
          <Link to="/contact" className="footer-contact-link">Request a callback</Link>
        </div>
      </div>

      <nav className="container footer-policies" aria-label="Policies and buyer information">
        <Link to="/terms">Terms & conditions</Link><Link to="/privacy">Privacy policy</Link><Link to="/cookies">Cookie notice</Link><Link to="/buying-guide">Buyer guide & FAQs</Link>
      </nav>
      <div className="container footer-bottom">
        <p>© {year} <span lang={textLang(site.businessName)}>{site.businessName}</span>. All rights reserved.</p>
        <div className="footer-bottom-actions">
          <span lang="my">မြန်မာနိုင်ငံ</span>
          <span className="footer-dot" aria-hidden="true" />
          <button type="button" onClick={backToTop}>
            Back to top <ArrowUp aria-hidden="true" />
          </button>
        </div>
      </div>
    </footer>
  );
}
